/**
 * Checkbox - Styled checkbox with label
 *
 * Use with React Hook Form:
 * <Checkbox label="I agree to the terms" {...register('terms')} />
 *
 * With error:
 * <Checkbox label="Accept terms" error={errors.terms?.message} {...register('terms')} />
 */

import { Check } from 'lucide-react';
import { forwardRef } from 'react';

import { cn } from '../utils';

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Checkbox label */
  label: string;
  /** Error message from form validation */
  error?: string;
  /** Optional description shown below the label */
  description?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, error, description, className, ...props }, ref) => {
    return (
      <div className={className}>
        <label
          className={cn('flex gap-3 cursor-pointer', description ? 'items-start' : 'items-center')}
        >
          <div className={cn('relative flex-shrink-0', description && 'mt-0.5')}>
            <input ref={ref} type="checkbox" className="peer sr-only" {...props} />
            <div
              className={cn(
                'h-5 w-5 rounded border transition-all',
                'bg-gray-50 dark:bg-[#18181b]',
                'peer-focus-visible:ring-2 peer-focus-visible:ring-blue-500 peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-white dark:peer-focus-visible:ring-offset-[#09090b]',
                'peer-checked:border-blue-500 peer-checked:bg-blue-500',
                'peer-disabled:cursor-not-allowed peer-disabled:opacity-50',
                error ? 'border-red-500' : 'border-gray-300 dark:border-white/[0.06]'
              )}
            />
            <Check className="pointer-events-none absolute left-0.5 top-0.5 h-4 w-4 text-white opacity-0 transition-opacity peer-checked:opacity-100" />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-medium text-gray-700 dark:text-zinc-300">{label}</span>
            {description && (
              <span className="text-sm text-gray-500 dark:text-zinc-500">{description}</span>
            )}
          </div>
        </label>
        {error && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';
